import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import healthService from '../../services/healthService';
import studentsService from '../../services/studentsService';
import DialogModal from '../../components/DialogModal';
import { MagnifyingGlass, Plus, PencilSimple, Trash } from '@phosphor-icons/react';

const emptyForm = {
  student_id: '',
  vaccine_name: '',
  date_administered: new Date().toISOString().split('T')[0],
  due_next_date: '',
  administered_by: '',
  notes: '',
};

function ImmunizationsPage() {
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredRecords(records);
      return;
    }
    const term = searchTerm.toLowerCase();
    setFilteredRecords(
      records.filter(
        (r) => getStudentName(r.student_id).toLowerCase().includes(term) || r.vaccine_name.toLowerCase().includes(term)
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records, searchTerm, students]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [recordsData, studentsData] = await Promise.all([
        healthService.listImmunizations(),
        studentsService.listStudents().catch(() => []),
      ]);
      setRecords(Array.isArray(recordsData) ? recordsData : []);
      setStudents(Array.isArray(studentsData) ? studentsData : studentsData?.items || studentsData?.students || []);
    } catch (error) {
      toast.error('Failed to load immunization records');
    } finally {
      setLoading(false);
    }
  };

  const getStudentName = (studentId) => {
    const student = students.find((s) => s.student_id === studentId);
    return student ? `${student.first_name} ${student.last_name}` : studentId;
  };

  const openCreateModal = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEditModal = (record) => {
    setEditingId(record.id);
    setFormData({
      student_id: record.student_id,
      vaccine_name: record.vaccine_name,
      date_administered: record.date_administered,
      due_next_date: record.due_next_date || '',
      administered_by: record.administered_by || '',
      notes: record.notes || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.student_id || !formData.vaccine_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      if (editingId) {
        const { student_id, ...rest } = formData;
        await healthService.updateImmunization(editingId, rest);
        toast.success('Immunization record updated');
      } else {
        await healthService.createImmunization(formData);
        toast.success('Immunization record added');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to save immunization record');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await healthService.deleteImmunization(deleteTarget);
      toast.success('Immunization record deleted');
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete immunization record');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading immunization records...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Health — Immunizations</h1>
            <p>Vaccination history and due dates</p>
          </div>
          <button className="btn btn-primary" onClick={openCreateModal}>
            <Plus size={20} />
            Add Immunization
          </button>
        </div>

        <div className="filters-bar">
          <div className="search-input">
            <MagnifyingGlass size={20} />
            <input
              type="text"
              placeholder="Search by student or vaccine..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="table-container">
          {filteredRecords.length === 0 ? (
            <div className="empty-state">
              <p>No immunization records found</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Vaccine</th>
                  <th>Date Administered</th>
                  <th>Next Due</th>
                  <th>Administered By</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map((record) => (
                  <tr key={record.id}>
                    <td>{getStudentName(record.student_id)}</td>
                    <td>{record.vaccine_name}</td>
                    <td>{record.date_administered}</td>
                    <td>{record.due_next_date || '-'}</td>
                    <td>{record.administered_by || '-'}</td>
                    <td className="action-buttons">
                      <button className="btn-icon" title="Edit" onClick={() => openEditModal(record)}>
                        <PencilSimple size={18} />
                      </button>
                      <button
                        className="btn-icon btn-danger"
                        title="Delete"
                        onClick={() => setDeleteTarget(record.id)}
                      >
                        <Trash size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingId ? 'Edit Immunization Record' : 'Add Immunization Record'}</h2>
              <button type="button" className="btn-close" onClick={() => setShowModal(false)} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleSubmit}>
              {!editingId && (
                <div className="form-group">
                  <label>Student *</label>
                  <select value={formData.student_id} onChange={(e) => setFormData({ ...formData, student_id: e.target.value })} required>
                    <option value="">Select Student</option>
                    {students.map((s) => (
                      <option key={s.id} value={s.student_id}>{s.first_name} {s.last_name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>Vaccine Name *</label>
                <input type="text" value={formData.vaccine_name} onChange={(e) => setFormData({ ...formData, vaccine_name: e.target.value })} placeholder="e.g., Yellow Fever" required />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date Administered *</label>
                  <input type="date" value={formData.date_administered} onChange={(e) => setFormData({ ...formData, date_administered: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Next Due Date</label>
                  <input type="date" value={formData.due_next_date} onChange={(e) => setFormData({ ...formData, due_next_date: e.target.value })} />
                </div>
              </div>

              <div className="form-group">
                <label>Administered By</label>
                <input type="text" value={formData.administered_by} onChange={(e) => setFormData({ ...formData, administered_by: e.target.value })} />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : editingId ? 'Update' : 'Add'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DialogModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Immunization Record"
        message="This will permanently delete this immunization record. Continue?"
        type="warning"
        isLoading={deleteLoading}
        primaryAction={{ label: 'Delete', onClick: confirmDelete, loadingLabel: 'Deleting...' }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </Layout>
  );
}

export default ImmunizationsPage;
